import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { validationErrorMessage } from './scheduleFormat.ts';
import { useToaster } from '../../../core/components/Toast.tsx';
import {
    DELETE_CONVERSATION_SCHEDULE,
    GET_CONVERSATION_SCHEDULES,
    RUN_SCHEDULE_NOW,
    UPDATE_CONVERSATION_SCHEDULE,
} from '../../queries.ts';

type Schedule = { id: string; name: string; conversationTitle?: string | null };

/** The ⋮ actions of a schedule (card and detail page): pause, resume, run now and delete, each with its toast. */
export const useScheduleActions = () => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const refetch = { refetchQueries: [{ query: GET_CONVERSATION_SCHEDULES }] };
    const [updateSchedule, { loading: updating }] = useMutation(UPDATE_CONVERSATION_SCHEDULE, refetch);
    const [runScheduleNow, { loading: running }] = useMutation(RUN_SCHEDULE_NOW, refetch);
    const [deleteSchedule, { loading: deleting }] = useMutation(DELETE_CONVERSATION_SCHEDULE, refetch);

    const run = async (action: () => Promise<unknown>, success: () => void) => {
        try {
            await action();
            success();
            return true;
        } catch (e) {
            publishToast({ description: validationErrorMessage(t, e, 'automations.toasts.error'), status: 'error' });
            return false;
        }
    };

    const setActive = (schedule: Schedule, active: boolean) =>
        run(
            () => updateSchedule({ variables: { id: schedule.id, active } }),
            () =>
                publishToast({
                    description: t(active ? 'automations.toasts.resumed' : 'automations.toasts.paused', {
                        name: schedule.name,
                    }),
                    status: 'success',
                })
        );

    return {
        pause: (schedule: Schedule) => setActive(schedule, false),
        resume: (schedule: Schedule) => setActive(schedule, true),
        // a real run, delivered to every destination (unlike the dialog's test run, which stays in the chat)
        runNow: (schedule: Schedule) =>
            run(
                () => runScheduleNow({ variables: { id: schedule.id } }),
                () =>
                    publishToast({
                        title: t('automations.toasts.runStarted', { name: schedule.name }),
                        description: schedule.conversationTitle
                            ? t('automations.toasts.runStartedText', { chat: schedule.conversationTitle })
                            : undefined,
                        status: 'success',
                    })
            ),
        remove: (schedule: Schedule) =>
            run(
                async () => {
                    const { data } = await deleteSchedule({ variables: { id: schedule.id } });
                    if (!data?.deleteAgentConversationSchedule?.success) throw new Error('not deleted');
                },
                () =>
                    publishToast({
                        description: t('automations.toasts.deleted', { name: schedule.name }),
                        status: 'success',
                    })
            ),
        busy: updating || running || deleting,
    };
};
